import React, { useState, useEffect } from 'react';
import useAxios from "utils/useAxios";
import { getLocalidades } from "api/modules/localidades.api";
import { getEscuelasByLocalidad } from "api/modules/escuelas.api";

const LocationSelector = ({
  isReadonly,
  onLocalidadChange,
  onEscuelaChange,
  onMesaChange,
  actasCargadas = [],
  isEditing = false,
  initialValues = {},
  forceReset = false,
  onResetComplete
}) => {
  const [localidades, setLocalidades] = useState([]);
  const [escuelas, setEscuelas] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [isLoadingLocalidades, setIsLoadingLocalidades] = useState(true);
  const [isLoadingEscuelas, setIsLoadingEscuelas] = useState(false);
  const [errorLocalidades, setErrorLocalidades] = useState(null);
  const [errorEscuelas, setErrorEscuelas] = useState(null);

  // Estados locales para los valores seleccionados
  const [selectedLocalidad, setSelectedLocalidad] = useState(null);
  const [selectedEscuela, setSelectedEscuela] = useState(null);
  const [selectedMesa, setSelectedMesa] = useState(null);

  // Función para filtrar localidades que tienen escuelas disponibles
  const filtrarLocalidadesDisponibles = (localidadesList) => {
    if (!actasCargadas || actasCargadas.length === 0) {
      return localidadesList; // Si no hay actas cargadas, mostrar todas
    }

    return localidadesList.filter(localidad => {
      // Verificar si esta localidad tiene escuelas con mesas disponibles
      // Por ahora, asumimos que todas las localidades tienen escuelas disponibles
      // En una implementación más avanzada, podrías verificar esto contra la base de datos
      return true;
    });
  };

  // Función para filtrar escuelas que tienen mesas disponibles
  const filtrarEscuelasDisponibles = (escuelasList) => {
    if (!actasCargadas || actasCargadas.length === 0) {
      return escuelasList; // Si no hay actas cargadas, mostrar todas
    }

    // En modo edición, mostrar todas las escuelas (incluyendo la actual)
    if (isEditing) {
      return escuelasList;
    }

    return escuelasList.filter(escuela => {
      // Verificar si esta escuela tiene mesas disponibles
      const mesasOcupadas = actasCargadas.filter(acta =>
        acta.localidad === selectedLocalidad &&
        acta.escuela === escuela.id
      );

      // Si todas las mesas de esta escuela están ocupadas, no mostrar la escuela
      const totalMesas = escuela.max_mesa - escuela.min_mesa + 1;
      const tieneMesasDisponibles = mesasOcupadas.length < totalMesas;


      return tieneMesasDisponibles;
    });
  };

  // Función para filtrar mesas disponibles
  const filtrarMesasDisponibles = (mesasList) => {
    if (!actasCargadas || actasCargadas.length === 0) {
      return mesasList; // Si no hay actas cargadas, mostrar todas
    }

    // En modo edición, mostrar todas las mesas (incluyendo la actual)
    if (isEditing) {
      return mesasList;
    }

    const mesasDisponibles = mesasList.filter(mesa => {
      // Verificar si esta mesa ya está cargada
      const mesaOcupada = actasCargadas.find(acta =>
        acta.localidad === selectedLocalidad &&
        acta.escuela === selectedEscuela &&
        String(acta.mesa) === String(mesa) // Convertir ambos a string para comparar
      );

      return !mesaOcupada; // Mostrar solo las mesas no ocupadas
    });


    return mesasDisponibles;
  };

  // Obtener localidades
  const { onSubmit: fetchLocalidades } = useAxios({
    submitFn: getLocalidades,
    onSuccess: (response) => {
      try {

        // La API devuelve { "data": [...] }, necesitamos acceder a response.data
        const localidadesArray = Array.isArray(response?.data?.data) ? response.data.data : [];

        setLocalidades(localidadesArray);
        setErrorLocalidades(null);
      } catch (error) {

        setErrorLocalidades('Error al procesar localidades');
        setLocalidades([]);
      } finally {
        setIsLoadingLocalidades(false);
      }
    },
    onError: (error) => {

      setErrorLocalidades('Error al cargar localidades');
      setLocalidades([]);
      setIsLoadingLocalidades(false);
    }
  });

  // Obtener escuelas por localidad
  const { onSubmit: fetchEscuelas } = useAxios({
    submitFn: getEscuelasByLocalidad,
    onSuccess: (response) => {
      try {

        // La nueva API devuelve directamente el array de escuelas
        const escuelasArray = Array.isArray(response?.data) ? response.data : [];

        setEscuelas(escuelasArray);
        setErrorEscuelas(null);

        // Si no hay escuelas, mostrar mensaje informativo
        if (escuelasArray.length === 0) {

        }
      } catch (error) {

        setErrorEscuelas('Error al procesar escuelas');
        setEscuelas([]);
      } finally {
        setIsLoadingEscuelas(false);
      }
    },
    onError: (error) => {

      // En caso de error, establecer escuelas como array vacío
      setEscuelas([]);
      setErrorEscuelas('Error al cargar escuelas');
      setIsLoadingEscuelas(false);
    }
  });

  useEffect(() => {
    try {

      fetchLocalidades();
    } catch (error) {

      setErrorLocalidades('Error al iniciar carga de localidades');
      setIsLoadingLocalidades(false);
    }
  }, []);

  // Manejar forceReset - resetear estados internos cuando se solicite
  useEffect(() => {

    if (forceReset) {

      
      setSelectedLocalidad(null);
      setSelectedEscuela(null);
      setSelectedMesa(null);
      setEscuelas([]);
      setMesas([]);
      

      
      // Notificar que el reset está completo
      if (onResetComplete) {

        onResetComplete();
      }
    }
  }, [forceReset, onResetComplete, selectedLocalidad, selectedEscuela, selectedMesa]);

  // Establecer valores iniciales en modo edición
  useEffect(() => {
    if (isEditing && initialValues && !forceReset) {
      // Modo edición: cargar valores del acta
      setSelectedLocalidad(initialValues.localidad || null);
      setSelectedEscuela(initialValues.escuela || null);
      setSelectedMesa(initialValues.mesa || null);

      // Cargar escuelas si hay localidad
      if (initialValues.localidad) {
        setIsLoadingEscuelas(true);
        setErrorEscuelas(null);
        fetchEscuelas(initialValues.localidad);
      }
    }
  }, [initialValues, isEditing, forceReset]);

  // Generar mesas cuando se cargan las escuelas en modo edición
  useEffect(() => {
    if (isEditing && selectedEscuela && escuelas.length > 0) {
      const escuela = escuelas.find(e => e.id === selectedEscuela);
      if (escuela && escuela.min_mesa !== undefined && escuela.max_mesa !== undefined) {
        const mesasArray = [];
        for (let i = escuela.min_mesa; i <= escuela.max_mesa; i++) {
          mesasArray.push(i);
        }
        setMesas(mesasArray);
      }
    }
  }, [isEditing, selectedEscuela, escuelas]);

  const handleLocalidadChange = (e) => {
    try {
      const localidadId = parseInt(e.target.value, 10) || null;

      // Actualizar estado local
      setSelectedLocalidad(localidadId);
      setSelectedEscuela(null);
      setSelectedMesa(null);

      // Llamar a la función del padre para manejar el estado
      if (onLocalidadChange) {
        onLocalidadChange(localidadId);
      }

      // Limpiar arrays dependientes
      setEscuelas([]);
      setMesas([]);

      // Cargar escuelas directamente si hay localidad seleccionada
      if (localidadId) {
        setIsLoadingEscuelas(true);
        setErrorEscuelas(null);
        fetchEscuelas(localidadId);
      }
    } catch (error) {
      console.error('❌ Error en handleLocalidadChange:', error);
    }
  };

  const handleEscuelaChange = (e) => {
    try {
      const escuelaId = parseInt(e.target.value, 10) || null;

      // Actualizar estado local
      setSelectedEscuela(escuelaId);
      setSelectedMesa(null);

      // Llamar a la función del padre para manejar el estado
      if (onEscuelaChange) {
        onEscuelaChange(escuelaId);
      }

      // Limpiar mesas
      setMesas([]);

      // Generar mesas directamente si hay escuela seleccionada
      if (escuelaId && escuelas.length > 0) {

        const escuela = escuelas.find(e => e.id === escuelaId);
        if (escuela && escuela.min_mesa !== undefined && escuela.max_mesa !== undefined) {
          const mesasArray = [];
          for (let i = escuela.min_mesa; i <= escuela.max_mesa; i++) {
            mesasArray.push(i);
          }
          setMesas(mesasArray);

        }
      }
    } catch (error) {
      console.error('❌ Error en handleEscuelaChange:', error);
    }
  };

  const handleMesaChange = (e) => {
    try {
      const mesa = parseInt(e.target.value, 10) || null;

      // Actualizar estado local
      setSelectedMesa(mesa);

      // Llamar a la función del padre para manejar el estado
      if (onMesaChange) {
        onMesaChange(mesa);
      }
    } catch (error) {
      console.error('❌ Error en handleMesaChange:', error);
    }
  };

  // Si hay errores críticos, mostrar mensaje pero no cerrar la app
  if (errorLocalidades && localidades.length === 0 && !isLoadingLocalidades) {
    return (
      <div className="row mb-4">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {errorLocalidades}. Por favor, recarga la página.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row mb-4">
      {/* Localidad */}
      <div className="col-md-4">
        <div className="d-flex flex-column h-100">
          <label className="form-label fw-bold mb-2 text-dark">
            <i className="fas fa-map-marker-alt me-2"></i>
            Localidad
          </label>
          <select
            className="form-select flex-grow-1 location-filter-select"
            value={selectedLocalidad || ''}
            onChange={handleLocalidadChange}
            disabled={isReadonly || isLoadingLocalidades || isEditing}
            // Debug: mostrar valor actual del dropdown

          >
            <option value="">
              {isLoadingLocalidades ? 'Cargando...' : 'Seleccionar localidad'}
            </option>
            {localidades && localidades.length > 0 ? (
              filtrarLocalidadesDisponibles(localidades).map(localidad => (
                <option key={localidad.id} value={localidad.id}>
                  {localidad.circuito} - {localidad.descripcion}
                </option>
              ))
            ) : (
              <option value="" disabled>
                {isLoadingLocalidades ? 'Cargando localidades...' : 'No hay localidades disponibles'}
              </option>
            )}
          </select>
          {errorLocalidades && (
            <small className="form-text text-danger mt-1">
              <i className="fas fa-exclamation-circle me-1"></i>
              {errorLocalidades}
            </small>
          )}
        </div>
      </div>
      
      {/* Escuela */}
      <div className="col-md-4">
        <div className="d-flex flex-column h-100">
          <label className="form-label fw-bold mb-2 text-dark">
            <i className="fas fa-school me-2"></i>
            Escuela
          </label>
          <select
            className="form-select flex-grow-1 location-filter-select"
            value={selectedEscuela || ''}
            onChange={handleEscuelaChange}
            disabled={!selectedLocalidad || isReadonly || isLoadingEscuelas || isEditing}
            // Debug: mostrar valor actual del dropdown
          >
            <option value="">
              {isLoadingEscuelas ? 'Cargando...' : 'Seleccionar escuela'}
            </option>
            {escuelas && escuelas.length > 0 ? (
              filtrarEscuelasDisponibles(escuelas).map(escuela => (
                <option key={escuela.id} value={escuela.id}>
                  {escuela.nombre}
                </option>
              ))
            ) : (
              <option value="" disabled>
                {!selectedLocalidad ? 'Seleccione localidad primero' :
                  isLoadingEscuelas ? 'Cargando escuelas...' : 'No hay escuelas disponibles'}
              </option>
            )}
          </select>
          {selectedLocalidad && escuelas.length === 0 && !isLoadingEscuelas && !errorEscuelas && (
            <small className="form-text text-warning mt-1">
              <i className="fas fa-exclamation-triangle me-1"></i>
              Esta localidad no tiene escuelas registradas
            </small>
          )}
          {errorEscuelas && (
            <small className="form-text text-danger mt-1">
              <i className="fas fa-exclamation-circle me-1"></i>
              {errorEscuelas}
            </small>
          )}
        </div>
      </div>
      
      {/* Mesa */}
      <div className="col-md-4">
        <div className="d-flex flex-column h-100">
          <label className="form-label fw-bold mb-2 text-dark">
            <i className="fas fa-list-ol me-2"></i>
            Mesa
          </label>
          <select
            className="form-select flex-grow-1 location-filter-select"
            value={selectedMesa || ''}
            onChange={handleMesaChange}
            disabled={!selectedEscuela || isReadonly || isEditing}
            // Debug: mostrar valor actual del dropdown
          >
            <option value="">Seleccionar mesa</option>
            {mesas && mesas.length > 0 ? (
              filtrarMesasDisponibles(mesas).map(mesa => (
                <option key={mesa} value={mesa}>
                  {mesa}
                </option>
              ))
            ) : (
              <option value="" disabled>
                {!selectedEscuela ? 'Seleccione escuela primero' : 'No hay mesas disponibles'}
              </option>
            )}
          </select>
          {selectedEscuela && mesas.length === 0 && (
            <small className="form-text text-warning mt-1">
              <i className="fas fa-exclamation-triangle me-1"></i>
              Esta escuela no tiene mesas configuradas
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationSelector;
